import React, { useCallback, useMemo } from 'react';

import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Stack } from '@mui/material';

import { Experiences as EC } from '@luxuryescapes/contract-svc-experience';

import { getChainedValues } from '~/utils/ticketModeUtils';

interface Props {
  field: EC.TicketModeField;
  selectedOptions: Record<string, string | undefined>;
  onChange: (updated: Record<string, string | undefined>) => void;
  disabled?: boolean;
}

function TicketModeSelect({ field, selectedOptions, onChange, disabled }: Props) {
  const selectedOption = useMemo(
    () => field.options.find((option) => option.key === selectedOptions[field.key]),
    [field, selectedOptions],
  );

  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newKey = event.target.value || undefined;
      // Pass the full chained map (including undefined values) so the parent
      // can spread it correctly without restoring stale values
      onChange(getChainedValues(selectedOptions, field, newKey));
    },
    [field, selectedOptions, onChange],
  );

  const handleChildChange = useCallback(
    (updated: Record<string, string | undefined>) => {
      onChange({ ...selectedOptions, ...updated });
    },
    [selectedOptions, onChange],
  );

  return (
    <Stack spacing={1}>
      <FormControl size="small" disabled={disabled} sx={{ display: 'inline-flex', minWidth: 160, maxWidth: 400 }}>
        <InputLabel id={`ticket-mode-label-${field.key}`}>{field.text}</InputLabel>
        <Select
          labelId={`ticket-mode-label-${field.key}`}
          id={`ticket-mode-${field.key}`}
          value={selectedOptions[field.key] ?? ''}
          label={field.text}
          onChange={handleChange}
          required={field.required}
        >
          {!field.required && (
            <MenuItem value="">
              <em>Any</em>
            </MenuItem>
          )}
          {field.options.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              {option.text}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedOption?.fields?.map((childField) => (
        <TicketModeSelect
          key={`${selectedOption.key}-${childField.key}`}
          field={childField}
          selectedOptions={selectedOptions}
          onChange={handleChildChange}
          disabled={disabled}
        />
      ))}
    </Stack>
  );
}

export default TicketModeSelect;
